import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import { t } from "i18next";
import FetchContent from "../dynamic/fetchContent";
import Content from "../dynamic/Content";
import useIsMobile from "../../hooks/useIsMobile";

const WEBSITE_URL = "/websitejson";
const TITLE = "calendar-settings";
const ART = "en";

const COLUMN_COLORS = [
  "var(--color-headings-step-4)",
  "var(--color-headings-step-3)",
  "var(--color-headings-step-2)",
  "var(--color-headings-step-1)",
];

const SeasonalPricing = () => {
  const { username, auth } = useAuth();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState("low");

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(WEBSITE_URL, {
          params: { username, art: ART, title: TITLE },
        });

        if (res.data.entry?.content) {
          const parsed = JSON.parse(res.data.entry.content);
          setData(parsed);
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchPricing();
  }, [username]);

  if (!data) return null;

  const { pricing, currencyUsed } = data;

  const getMinNight = (type) => {
    if (typeof data.minNights === "number") return data.minNights;
    return data.minNights?.[type] ?? "-";
  };

  const seasons = [
    {
      key: "low",
      tabLabel: "Low",
      title: "Low Season",
      color: COLUMN_COLORS[1],
      values: [
        pricing.lowSeason.maxPeoplePrice,
        `${currencyUsed} ${pricing.lowSeason.extraCostPerPerson}`,
        `${currencyUsed} ${pricing.lowSeason.weekendExtraCost}`,
        getMinNight("low"),
      ],
      amount: pricing.lowSeason.amount,
    },
    {
      key: "shoulder",
      tabLabel: "Shoulder",
      title: "Shoulder Season",
      color: COLUMN_COLORS[2],
      values: [
        pricing.basePrice.maxPeoplePrice,
        `${currencyUsed} ${pricing.basePrice.extraCostPerPerson}`,
        `${currencyUsed} ${pricing.basePrice.weekendExtraCost}`,
        getMinNight("base"),
      ],
      amount: pricing.basePrice.amount,
    },
    {
      key: "high",
      tabLabel: "High",
      title: "High Season",
      color: COLUMN_COLORS[3],
      values: [
        pricing.highSeason.maxPeoplePrice,
        `${currencyUsed} ${pricing.highSeason.extraCostPerPerson}`,
        `${currencyUsed} ${pricing.highSeason.weekendExtraCost}`,
        getMinNight("high"),
      ],
      amount: pricing.highSeason.amount,
    },
  ];

  const activeSeason =
    seasons.find((season) => season.key === selectedSeason) || seasons[0];

  return (
    <div className="container">
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <div className="contact-title">
            <h1>{t("seasonal-pricing")}</h1>
        </div>

        {!auth?.role ? (
          <div className="p-brandnew">
            <FetchContent title="seasonal-pricing-p" />
          </div>
        ) : (
          <div className="p-brandnew">
            <Content title={"seasonal-pricing-p"} styling="p" />
          </div>
        )}
      </div>

      {isMobile ? (
        <div
          style={{
            margin: "0 auto",
            fontFamily: "var(--font-fm-ss)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "6px",
              marginBottom: "6px",
              alignItems: "stretch",
            }}
          >
            {seasons.map((season) => (
              <button
                key={season.key}
                onClick={() => setSelectedSeason(season.key)}
                style={{
                  width: "100%",
                  minHeight: "38px",
                  padding: "7px 6px",
                  border: "none",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  background: "rgba(0,0,0,0.25)",
                  color:
                    selectedSeason === season.key
                      ? "#1e63d6"
                      : "rgba(255,255,255,0.9)",
                  fontWeight: "700",
                  lineHeight: "1",
                  cursor: "pointer",
                }}
              >
                {season.tabLabel}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: "6px" }}>
            <HeaderCard
              title={activeSeason.title}
              price={activeSeason.amount}
              currency={currencyUsed}
              background={activeSeason.color}
              compact={true}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2.3fr 1fr",
              gap: "2px",
            }}
          >
            <MobileRow
              label="Max People For Base Price"
              value={activeSeason.values[0]}
              valueBackground={activeSeason.color}
            />
            <MobileRow
              label="Extra Cost Per Person"
              value={activeSeason.values[1]}
              valueBackground={activeSeason.color}
            />
            <MobileRow
              label={
                <>
                  Extra Cost In Weekends <br />
                  <span style={{ fontSize: "18px", opacity: 0.7 }}>
                    Friday / Saturday / Sunday
                  </span>
                </>
              }
              value={activeSeason.values[2]}
              valueBackground={activeSeason.color}
            />
            <MobileRow
              label="Minimum Nights Of Stay Per Season"
              value={activeSeason.values[3]}
              valueBackground={activeSeason.color}
            />
          </div>
        </div>
      ) : (
        <div
          style={{
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
            gap: "10px",
            borderRadius: "8px",
            overflow: "hidden",
            fontFamily: "var(--font-fm-ss)", 
          }}
        >
          <div
            style={{
              background: "transparent",
            }}
          ></div>

          {/* HEADERS */}
          <HeaderCard
            title="Low Season"
            price={pricing.lowSeason.amount}
            currency={currencyUsed}
            background={COLUMN_COLORS[1]}
          />

          <HeaderCard
            title="Shoulder Season"
            price={pricing.basePrice.amount}
            currency={currencyUsed}
            background={COLUMN_COLORS[2]}
          />

          <HeaderCard
            title="High Season"
            price={pricing.highSeason.amount}
            currency={currencyUsed}
            background={COLUMN_COLORS[3]}
          />

          {/* ROWS */}
          <Row
            label="Max People For Base Price"
            values={[
              pricing.lowSeason.maxPeoplePrice,
              pricing.basePrice.maxPeoplePrice,
              pricing.highSeason.maxPeoplePrice,
            ]}
          />

          <Row
            label="Extra Cost Per Person"
            values={[
              `${currencyUsed} ${pricing.lowSeason.extraCostPerPerson}`,
              `${currencyUsed} ${pricing.basePrice.extraCostPerPerson}`,
              `${currencyUsed} ${pricing.highSeason.extraCostPerPerson}`,
            ]}
          />

          <Row
            label={
              <>
                Extra Cost In Weekends <br />
                <span style={{ fontSize: "18px", opacity: 0.7 }}>
                  Friday / Saturday / Sunday
                </span>
              </>
            }
            values={[
              `${currencyUsed} ${pricing.lowSeason.weekendExtraCost}`,
              `${currencyUsed} ${pricing.basePrice.weekendExtraCost}`,
              `${currencyUsed} ${pricing.highSeason.weekendExtraCost}`,
            ]}
          />

          <Row
            label="Minimum Nights Of Stay"
            values={[
              getMinNight("low"),
              getMinNight("base"),
              getMinNight("high"),
            ]}
          />
        </div>
      )}
    </div>
  );
};

/* HEADER */
const HeaderCard = ({ title, price, currency, background, compact = false }) => {
  return (
    <div
      style={{
        padding: compact ? "14px" : "20px",
        textAlign: "center",
        background,
      }}
    >
      <div style={{ marginBottom: compact ? "3px" : "5px", fontWeight: "700", fontSize: compact ? "24px" : "28px" }}>{title}</div>

      <p style={{ fontSize: compact ? "16px" : "16px", fontWeight: "700", opacity: 0.7 }}>
        {currency} {price} / base price
      </p>
    </div>
  );
};

/* ROW */
const Row = ({ label, values }) => {
  return (
    <>
      <div
        style={{
          fontSize: "18px",
          padding: "14px",
          background: COLUMN_COLORS[0],
          fontWeight: "600",
          
        }}
      >
        {label}
      </div>

      {values.map((val, i) => (
  <div
    key={i}
    style={{
      padding: "14px",
      textAlign: "center",
      fontSize: "22px",
      fontWeight: "400",
      background: COLUMN_COLORS[i + 1],
    }}
  >
    {val}
  </div>
))}
    </>
  );
};

const MobileRow = ({ label, value, valueBackground }) => {
  return (
    <>
      <div
        style={{
          fontSize: "18px",
          padding: "14px",
          background: COLUMN_COLORS[0],
          fontWeight: "600",
        }}
      >
        {label}
      </div>
      <div
        style={{
          padding: "14px",
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "400",
          background: valueBackground,
        }}
      >
        {value}
      </div>
    </>
  );
};

export default SeasonalPricing;
